import { useCallback, useContext } from "react";
import { ActivityContext } from "../context/ActivityContext";
import { eventService } from '../apps/events/services'
import { appName as eventsAppName, displayName as eventsDisplayName } from '../apps/events/appConfig'
import { apps } from '../apps'

// An event with no end date is treated as ending when it starts — same
// convention as EventList.js/EventCalendar.js's effectiveEnd.
const effectiveEnd = (event) => event.event_end_timestamp
  ? new Date(event.event_end_timestamp)
  : new Date(event.event_start_timestamp)

const dateOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

// Primary sort: date. Secondary (tie-break on same date): source app label,
// so items from the same day/app cluster together instead of interleaving
// in whatever order the native/external arrays happened to be concatenated.
const compareItems = (a, b) => {
  const dateDiff = new Date(a.event_start_timestamp || a.start) - new Date(b.event_start_timestamp || b.start)
  if (dateDiff !== 0) return dateDiff
  return (a.sourceLabel || '').localeCompare(b.sourceLabel || '', 'sv')
}

// Native "events" app rows tagged with the same source/label/color fields
// external items already carry, so the widget can render a uniform,
// colour-coded list regardless of where an item came from.
const tagNative = (event) => ({
  ...event,
  source: eventsAppName,
  sourceLabel: eventsDisplayName,
  sourceColor: 'var(--color-purple)',
})

export const useActivity = () => {
  const {
    events,
    setEvents,
    ongoingEvents,
    setOngoingEvents,
    externalEvents,
    setExternalEvents,
    daysAhead,
    setDaysAhead
  } = useContext(ActivityContext);

  const refreshEvents = useCallback(
    async (overrideDaysAhead) => {
      const effectiveDaysAhead = overrideDaysAhead !== undefined ? overrideDaysAhead : daysAhead
      const now = new Date()
      const pastStart = new Date(now.getTime() - effectiveDaysAhead * 24 * 60 * 60 * 1000)

      const [upcomingRes, pastRes] = await Promise.all([
        eventService.getAll(effectiveDaysAhead).catch(err => { console.log(err); return null }),
        eventService.getByRange(pastStart, now).catch(err => { console.log(err); return null }),
      ])

      if (upcomingRes) setEvents(upcomingRes.events || [])
      if (pastRes) {
        // getByRange is overlap-aware, so it also returns multi-day events
        // that started before `now` but haven't ended yet — that's what
        // "ongoing" means here, same split as EventList.js.
        setOngoingEvents((pastRes.events || [])
          .filter(e => effectiveEnd(e) >= now)
          .sort((a, b) => new Date(a.event_start_timestamp) - new Date(b.event_start_timestamp)))
      }

      // Cross-app aggregation: any app can opt in by exposing
      // `calendarEvents`/`calendarColor` on its appDefinition (see
      // kayentaStories/appDefinition.js for a real example). The events app
      // itself is skipped here — it's already fetched natively above.
      const contributingApps = Object.values(apps)
        .filter(app => app.name !== eventsAppName && typeof app.calendarEvents === 'function')

      const externalResults = await Promise.all(
        contributingApps.map(app =>
          app.calendarEvents({ daysAhead: effectiveDaysAhead })
            .then(items => (items || []).map(item => ({
              ...item,
              source: app.name,
              sourceLabel: app.displayName,
              sourceColor: app.calendarColor || 'var(--color-dark-grey)',
            })))
            .catch(err => { console.log(err); return [] })
        )
      )
      setExternalEvents(externalResults.flat())
    },
    [daysAhead, setEvents, setOngoingEvents, setExternalEvents]
  );

  const toggleInterest = useCallback(
    async (slug) => {
      const res = await eventService.toggleInterest(slug).catch(err => { console.log(err); return null })
      if (!res) return null
      const updater = list => list.map(e => e.event_slug === slug
        ? { ...e, interest_count: res.interest_count, user_has_marked_interest: res.user_has_marked_interest, interested_users: res.interested_users }
        : e
      )
      setEvents(updater)
      setOngoingEvents(updater)
      return res
    },
    [setEvents, setOngoingEvents]
  );

  // The same gesture as toggleInterest above, for an item that came from another app's
  // calendarEvents provider — an interview slot you can sign up for. The provider owns the
  // API call and normalizes its own response (see recruitment's helpers/calendarEvents.js),
  // so nothing app-specific appears here: we just patch the item back into place by its key
  // and hand the result to the caller, which decides whether to offer an .ics.
  //
  // Same accepted race as toggleInterest: a refreshEvents() already in flight will overwrite
  // this patch and revert the UI until the next 60s poll. Deliberately not papered over with
  // optimistic pre-patching, which would only widen the window.
  const toggleParticipation = useCallback(
    async (item) => {
      if (!item.participation) return null
      const res = await item.participation.toggle().catch(err => { console.log(err); return null })
      if (!res) return null
      setExternalEvents(list => list.map(i => i.key === item.key
        ? { ...i, participation: { ...i.participation, attendees: res.attendees, userIsAttending: res.userIsAttending } }
        : i
      ))
      return res
    },
    [setExternalEvents]
  );

  // Merge native + external items into the two buckets the widget renders,
  // each item uniformly tagged with source/sourceLabel/sourceColor — the
  // widget doesn't need to know which app anything came from to render it.
  const now = new Date()
  const today = dateOnly(now)

  const externalOngoing = externalEvents.filter(item => dateOnly(new Date(item.start)) <= today && dateOnly(new Date(item.end)) >= today)
  const externalUpcoming = externalEvents.filter(item => dateOnly(new Date(item.start)) > today)

  const ongoingItems = [...ongoingEvents.map(tagNative), ...externalOngoing].sort(compareItems)
  const upcomingItems = [...events.map(tagNative), ...externalUpcoming].sort(compareItems)

  return {
    events,
    ongoingEvents,
    ongoingItems,
    upcomingItems,
    daysAhead,
    setDaysAhead,
    refreshEvents,
    toggleInterest,
    toggleParticipation
  };
}
